import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Typography,
} from "@mui/material";
import { axiosInstance } from "../../../utils/axiosInstance";
import Message from "../../Alert/Message";

interface DiscordAlertTypeInfo {
  type: string;
  label: string;
  description: string;
}

interface DiscordAlertCatalog {
  webhookConfigured: boolean;
  types: DiscordAlertTypeInfo[];
}

export default function DiscordAlertsSettings() {
  const [message, setMessage] = useState<string | boolean>(false);
  const [fetching, setFetching] = useState(true);
  const [catalog, setCatalog] = useState<DiscordAlertCatalog | null>(null);
  const [mentionRole, setMentionRole] = useState(false);
  const [sendingType, setSendingType] = useState<string | null>(null);

  const load = useCallback(async () => {
    setFetching(true);
    try {
      const res = await axiosInstance.get<DiscordAlertCatalog>(
        "/settings/discord-alerts"
      );
      setCatalog(res.data);
    } catch (e: any) {
      console.error(e);
      setCatalog(null);
      setMessage(
        e?.response?.data?.message ||
          "Could not load Discord alert catalog from sqi-proxy."
      );
    }
    setFetching(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const sendTest = async (type: string, label: string) => {
    setSendingType(type);
    setMessage(false);
    try {
      const res = await axiosInstance.post<{
        ok: boolean;
        sent: boolean;
        title?: string;
        message?: string;
      }>("/settings/discord-alerts/test", {
        type,
        mentionRole,
      });
      if (res.data?.sent || res.data?.ok) {
        setMessage(
          `Sent TEST: ${res.data.title || label}. Check the Discord ops channel.`
        );
      } else {
        setMessage(
          res.data?.message || `Failed to send TEST alert (${label}).`
        );
      }
    } catch (e: any) {
      console.error(e);
      setMessage(
        e?.response?.data?.message ||
          e?.response?.data?.error ||
          `Failed to send TEST alert (${label}).`
      );
    }
    setSendingType(null);
  };

  return (
    <Box className="sq-settings-panel">
      <Message message={message} setMessage={setMessage} />
      <Card variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography variant="h6" component="h2" gutterBottom>
          Discord alerts (TEST)
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Send sample embeds for each live alert type through sqi-proxy. Footers
          are marked <code>TEST</code>. Role mentions stay off unless you enable
          them below — that will ping the configured Discord role in the real
          channel.
        </Typography>

        {fetching ? (
          <Typography variant="body2">Loading…</Typography>
        ) : (
          <>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Webhook on sqi-proxy:{" "}
              {catalog?.webhookConfigured ? "configured" : "not set"}
            </Typography>

            <FormControlLabel
              control={
                <Checkbox
                  checked={mentionRole}
                  onChange={(e) => setMentionRole(e.target.checked)}
                  disabled={!catalog?.webhookConfigured}
                />
              }
              label="Mention Discord role (where the live alert would)"
              sx={{ mb: 2, display: "flex" }}
            />

            <div>
              {(catalog?.types ?? []).map((item) => (
                <div key={item.type} className="sq-discord-row">
                  <div>
                    <Typography variant="subtitle2">{item.label}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {item.description}
                    </Typography>
                  </div>
                  <Button
                    variant="contained"
                    disabled={
                      !catalog?.webhookConfigured || sendingType !== null
                    }
                    onClick={() => void sendTest(item.type, item.label)}
                  >
                    {sendingType === item.type ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      "Send TEST"
                    )}
                  </Button>
                </div>
              ))}
            </div>

            {!catalog?.types?.length ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                No alert types returned. Is sqi-proxy reachable?
              </Typography>
            ) : null}
          </>
        )}
      </Card>
    </Box>
  );
}
